import type { ReactNode } from "react";
import { Component } from "react";

import styled from "styled-components";

import { colorsPalettes, setFont } from "@similarweb/styles";
import { Button, PlainTooltip } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";

interface ITableSelectionProps {
    onCloseClick?: () => void;
    selectedText?: string;
    addToGroupLabel?: string;
    groupSelectorElement: ReactNode;
    secondaryActions?: ReactNode;
    tooltipText?: string;
    className?: string;
    isVisible: boolean;
    showSeparator?: boolean;
    isDisabled?: boolean;
}

interface ITableSelectionState {
    shouldFadeOut: boolean;
}

interface ITableSelectionContainerProps {
    isVisible: boolean;
    isDisabled: boolean;
}

const TableSelectionContainer = styled.div<ITableSelectionContainerProps>`
    height: ${({ isVisible }) => (isVisible ? `56px` : `0px`)};
    max-height: 56px;
    min-height: 0px;
    overflow: hidden;
    width: 100%;
    background-color: ${({ isDisabled }) => (isDisabled ? colorsPalettes.carbon[50] : colorsPalettes.blue[400])};
    padding: 0 16px 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all ease 150ms;
    box-sizing: border-box;
`;

export const TableSelectionContent = styled.div`
    display: flex;
    align-items: center;
`;

export const TableSelectionSeparator = styled.div`
    background-color: ${colorsPalettes.carbon[0]};
    width: 1px;
    height: 36px;
    margin: 0 12px;
    display: flex;
    align-items: center;
`;

const TableSelectionCount = styled.div<{ isDisabled: boolean }>`
    ${({ isDisabled }) =>
        setFont({
            $size: 16,
            $color: isDisabled ? colorsPalettes.carbon[300] : colorsPalettes.carbon[0],
        })}
`;

const TableSelectionAddToLabel = styled.div`
    ${setFont({ $size: 16, $color: colorsPalettes.carbon[0] })}
`;

export const TableSelectionSelector = styled.div`
    margin-left: 10px;
`;

export class TableSelection extends Component<ITableSelectionProps, ITableSelectionState> {
    static defaultProps = {
        selectedText: "",
        tooltipText: null,
        showSeparator: true,
    };

    state = {
        shouldFadeOut: this.props.isVisible,
    };

    componentDidUpdate() {
        if (this.props.isVisible && !this.state.shouldFadeOut) {
            this.setState({ shouldFadeOut: this.props.isVisible });
        }
    }

    render() {
        const {
            onCloseClick,
            selectedText,
            addToGroupLabel,
            groupSelectorElement,
            secondaryActions,
            tooltipText,
            isVisible,
            className,
            showSeparator,
            isDisabled = false,
        } = this.props;
        return (
            <TableSelectionContainer
                isVisible={isVisible}
                className={`TableSelection ${className}`}
                isDisabled={isDisabled}
            >
                <TableSelectionContent>
                    <TableSelectionCount isDisabled={isDisabled}>{selectedText}</TableSelectionCount>
                    {showSeparator ? <TableSelectionSeparator /> : null}
                    {addToGroupLabel ? <TableSelectionAddToLabel>{addToGroupLabel}</TableSelectionAddToLabel> : null}
                    <TableSelectionSelector>{groupSelectorElement}</TableSelectionSelector>
                </TableSelectionContent>
                <TableSelectionContent>
                    {secondaryActions}
                    {onCloseClick && !isDisabled ? (
                        <PlainTooltip
                            tooltipContent={tooltipText}
                            enabled={Boolean(tooltipText)}
                        >
                            <div
                                onClick={onCloseClick}
                                data-automation-close={true}
                            >
                                <Button type={"flatNegative"}>{i18nFilter()("table.selection.clear.all")}</Button>
                            </div>
                        </PlainTooltip>
                    ) : null}
                </TableSelectionContent>
            </TableSelectionContainer>
        );
    }
}
